import { api } from "./api.js";

window.THOMAS_APP_VERSION = "postgres-v2";
const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const WORK_DAY_MINUTES = 300;
// Don't show any outstanding work before this date — Thomas tracked jobs in
// a different system before then, so showing pre-epoch periods would just
// flood the list with phantom "overdue" work.
const APP_EPOCH = "2026-05-18";
const locationNames = [
  "Love Lane Play Area",
  "Playing Field",
  "Cleobury QEII Park",
  "Churchyard Area",
  "Memorial Gardens",
  "Wells",
  "Cemetery",
  "Bus Shelters",
  "Toilets",
  "Highway Maintenance",
  "Other work",
  "Admin"
];


let state = emptyState();
let emailConfigured = false;
const expandedJobs = new Set();
// Locations the user has explicitly collapsed in the Today/outstanding-work
// view. Survives renders within the session so toggling another tab and
// coming back doesn't re-open everything.
const collapsedLocations = new Set();
let notesFilter = "today";
let editingTemplateId = "";
let editingNoteId = "";

const els = {
  appShell: document.querySelector(".app-shell"),
  authGate: document.querySelector("#auth-gate"),
  authForm: document.querySelector("#auth-form"),
  authPassword: document.querySelector("#app-password"),
  authError: document.querySelector("#auth-error"),
  logoutApp: document.querySelector("#logout-app"),
  date: document.querySelector("#work-date"),
  dateDisplayText: document.querySelector("#date-display-text"),
  datePrev: document.querySelector("#date-prev"),
  dateNext: document.querySelector("#date-next"),
  dateToday: document.querySelector("#date-today"),
  tabs: document.querySelectorAll(".tab"),
  views: document.querySelectorAll(".view"),
  todaySubtitle: document.querySelector("#today-subtitle"),
  summary: document.querySelector("#summary-strip"),
  jobList: document.querySelector("#job-list"),
  periodProgressMount: document.querySelector("#period-progress-mount"),
  noteForm: document.querySelector("#note-form"),
  noteLocation: document.querySelector("#note-location"),
  noteText: document.querySelector("#note-text"),
  notePriority: document.querySelector("#note-priority"),
  noteJob: document.querySelector("#note-job"),
  noteSubmit: document.querySelector("#note-submit"),
  cancelNoteEdit: document.querySelector("#cancel-note-edit"),
  dictateNote: document.querySelector("#dictate-note"),
  speechHint: document.querySelector("#speech-hint"),
  notesFilters: document.querySelectorAll("[data-notes-filter]"),
  notesList: document.querySelector("#notes-list"),
  report: document.querySelector("#report-output"),
  copyReport: document.querySelector("#copy-report"),
  shareReport: document.querySelector("#share-report"),
  downloadReport: document.querySelector("#download-report"),
  emailDailyReport: document.querySelector("#email-daily-report"),
  emailWeeklyReport: document.querySelector("#email-weekly-report"),
  emailTimedReport: document.querySelector("#email-timed-report"),
  emailStatus: document.querySelector("#email-status"),
  pdfLink: document.querySelector("#pdf-link"),
  timesheetPanel: document.querySelector("#timesheet-panel"),
  configForm: document.querySelector("#config-form"),
  appPasswordSetting: document.querySelector("#app-password-setting"),
  resendFrom: document.querySelector("#resend-from"),
  dailyRecipients: document.querySelector("#daily-recipients"),
  weeklyRecipients: document.querySelector("#weekly-recipients"),
  timedRecipients: document.querySelector("#timed-recipients"),
  timedSendTime: document.querySelector("#timed-send-time"),
  // resendEndpoint removed — the endpoint is fixed at /api/send-report.
  workDayChecks: document.querySelectorAll("[data-work-day]"),
  workDayHours: document.querySelectorAll("[data-work-hours]"),
  weeklyHoursSummary: document.querySelector("#weekly-hours-summary"),
  templateList: document.querySelector("#template-list"),
  jobForm: document.querySelector("#job-form"),
  jobKind: document.querySelector("#job-kind"),
  jobCadence: document.querySelector("#job-cadence"),
  jobTitle: document.querySelector("#job-title"),
  jobArea: document.querySelector("#job-area"),
  jobDescription: document.querySelector("#job-description"),
  jobShared: document.querySelector("#job-shared"),
  jobTiming: document.querySelector("#job-timing"),
  jobDay: document.querySelector("#job-day"),
  jobMonthDay: document.querySelector("#job-month-day"),
  jobOnceDate: document.querySelector("#job-once-date"),
  jobTasks: document.querySelector("#job-tasks"),
  jobSubmit: document.querySelector("#job-submit"),
  cancelJobEdit: document.querySelector("#cancel-job-edit"),
  timingField: document.querySelector("#timing-field"),
  weeklyDayField: document.querySelector("#weekly-day-field"),
  periodDayField: document.querySelector("#period-day-field"),
  onceDateField: document.querySelector("#once-date-field"),
  resetDemo: document.querySelector("#reset-demo"),
  jobTemplate: document.querySelector("#job-template")
};

els.date.value = toDateInput(new Date());
els.jobOnceDate.value = els.date.value;
updateDateDisplay();

function shiftDate(days) {
  els.date.value = toDateInput(addDays(parseDate(els.date.value), days));
  updateDateDisplay();
  render();
}

function updateDateDisplay() {
  const date = parseDate(els.date.value);
  const today = toDateInput(new Date());
  const yesterday = toDateInput(addDays(new Date(), -1));
  const tomorrow = toDateInput(addDays(new Date(), 1));
  const selected = els.date.value;
  let prefix;
  if (selected === today) prefix = "Today · ";
  else if (selected === yesterday) prefix = "Yesterday · ";
  else if (selected === tomorrow) prefix = "Tomorrow · ";
  else prefix = "";
  // Drop the weekday when we already have a relative prefix — "Tomorrow"
  // implies it. Keeps the label short enough for narrow phone widths.
  const formatted = prefix
    ? date.toLocaleDateString("en-GB", { day: "numeric", month: "long" })
    : date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
  els.dateDisplayText.textContent = `${prefix}${formatted}`;
  els.dateToday.classList.toggle("is-current", selected === today);
}

els.authForm.addEventListener("submit", unlockApp);
els.logoutApp.addEventListener("click", lockApp);
els.tabs.forEach((button) => {
  button.addEventListener("click", () => switchTab(button.dataset.tab));
});

els.notesFilters.forEach((button) => {
  button.addEventListener("click", () => {
    notesFilter = button.dataset.notesFilter;
    renderNotes(selectedDate());
  });
});
els.date.addEventListener("change", () => { updateDateDisplay(); render(); });
els.datePrev.addEventListener("click", () => shiftDate(-1));
els.dateNext.addEventListener("click", () => shiftDate(1));
els.dateToday.addEventListener("click", () => {
  els.date.value = toDateInput(new Date());
  updateDateDisplay();
  render();
});
els.noteForm.addEventListener("submit", saveManualNote);
els.cancelNoteEdit.addEventListener("click", cancelNoteEdit);
els.dictateNote.addEventListener("click", () => dictateInto(els.noteText));
els.jobForm.addEventListener("submit", saveJobTemplate);
els.cancelJobEdit.addEventListener("click", cancelJobEdit);
els.jobCadence.addEventListener("change", renderScheduleControls);
els.jobCadence.addEventListener("change", syncOneOffDefaults);
els.jobTiming.addEventListener("change", renderScheduleControls);
els.copyReport.addEventListener("click", copyReport);
els.shareReport.addEventListener("click", shareReport);
els.downloadReport.addEventListener("click", downloadReportPdf);
els.emailDailyReport.addEventListener("click", () => sendReportEmail("daily"));
els.emailWeeklyReport.addEventListener("click", () => sendReportEmail("weekly"));
els.emailTimedReport.addEventListener("click", () => sendReportEmail("timed"));
els.configForm.addEventListener("submit", saveConfig);
els.workDayChecks.forEach((input) => input.addEventListener("change", refreshWorkingWeekSummary));
els.workDayHours.forEach((input) => input.addEventListener("input", refreshWorkingWeekSummary));
els.resetDemo.addEventListener("click", () => {
  alert("App data is now stored centrally in Postgres — use the deployment tooling to reset, not this button.");
});

window.addEventListener("cmt:locked", () => {
  els.authGate.classList.remove("is-hidden");
  els.appShell.classList.add("is-locked");
  els.authPassword.value = "";
});

boot();

function emptyState() {
  return {
    templates: [],
    completions: {},
    notes: [],
    workLocations: {},
    timeEntries: {},
    extraTimeEntries: {},
    config: defaultConfig(),
  };
}

async function boot() {
  renderScheduleControls();
  const status = await api.auth.status().catch(() => ({ unlocked: false }));
  if (status.unlocked) {
    await loadStateFromApi();
    els.authGate.classList.add("is-hidden");
    els.appShell.classList.remove("is-locked");
    render();
  } else {
    els.authGate.classList.remove("is-hidden");
    els.appShell.classList.add("is-locked");
    renderConfig();
  }
}

async function loadStateFromApi() {
  const snapshot = await api.loadState();
  state = {
    templates: (snapshot.templates || []).map(normalizeTemplate),
    completions: completionsFromApi(snapshot.completions || {}),
    notes: snapshot.notes || [],
    workLocations: workLocationsFromApi(snapshot.workLocations || {}),
    timeEntries: snapshot.timeEntries || {},
    extraTimeEntries: extrasFromApi(snapshot.extraTimeEntries || {}),
    config: configFromApi(snapshot.config),
  };
  emailConfigured = Boolean(snapshot.emailConfigured);
  renderConfig();
}

function completionsFromApi(map) {
  const out = {};
  Object.entries(map).forEach(([key, row]) => {
    const tasks = {};
    const completedAt = {};
    Object.entries(row.tasks || {}).forEach(([index, value]) => {
      if (value && typeof value === "object") {
        tasks[index] = Boolean(value.checked);
        if (value.date) completedAt[index] = value.date;
      } else {
        tasks[index] = Boolean(value);
      }
    });
    out[key] = { tasks, completedAt, templateId: row.templateId, period: row.period };
  });
  return out;
}

function workLocationsFromApi(map) {
  const out = {};
  Object.entries(map).forEach(([date, locations]) => {
    out[date] = {};
    (locations || []).forEach((location) => { out[date][location] = true; });
  });
  return out;
}

function extrasFromApi(map) {
  const out = {};
  Object.entries(map).forEach(([date, rows]) => {
    out[date] = {};
    (rows || []).forEach((row) => {
      if (!out[date][row.location]) out[date][row.location] = [];
      if (row.description) out[date][row.location].push(row.description);
    });
  });
  return out;
}

function configFromApi(row) {
  if (!row) return defaultConfig();
  const defaults = defaultConfig();
  return {
    workingDays: row.workingDays || defaults.workingDays,
    email: {
      from: row.emailFrom || defaults.email.from,
      dailyRecipients: row.dailyRecipients ?? "",
      weeklyRecipients: row.weeklyRecipients ?? "",
      timedRecipients: row.timedRecipients ?? "",
      timedSendTime: row.timedSendTime || defaults.email.timedSendTime,
    },
  };
}

function defaultConfig() {
  return {
    workingDays: {
      0: { enabled: false, minutes: 0 },
      1: { enabled: false, minutes: 0 },
      2: { enabled: true, minutes: WORK_DAY_MINUTES },
      3: { enabled: true, minutes: WORK_DAY_MINUTES },
      4: { enabled: true, minutes: WORK_DAY_MINUTES },
      5: { enabled: false, minutes: 0 },
      6: { enabled: false, minutes: 0 }
    },
    email: {
      from: "reports@cmt.preciseimpact.ai",
      dailyRecipients: "",
      weeklyRecipients: "",
      timedRecipients: "",
      timedSendTime: "17:00",
    }
  };
}

function normalizeTemplate(template) {
  const areaMap = {
    "Town centre": "Highway Maintenance",
    "High Street": "Highway Maintenance",
    "Riverside Park": "Cleobury QEII Park",
    "Whole town": "Highway Maintenance",
    "QE2 Park": "Cleobury QEII Park",
    "Play area": "Love Lane Play Area",
    "Dog field": "Playing Field",
    "Memorial garden": "Memorial Gardens",
    "Churchyard": "Churchyard Area",
    "Graveyard": "Cemetery",
    "Highways": "Highway Maintenance"
  };
  const normalized = {
    category: "routine",
    cadence: "weekly",
    timing: "flex",
    shared: false,
    userCreated: false,
    archived: false,
    archivedAt: "",
    replacesId: "",
    description: "",
    tasks: ["Check and record"],
    ...template,
    area: areaMap[template.area] || template.area || "Admin"
  };
  if (!Array.isArray(normalized.tasks) || !normalized.tasks.length) {
    normalized.tasks = [normalized.title || "Check and record"];
  }
  return normalized;
}

// No-op kept so legacy call sites compile. Mutations now fire targeted
// API calls at the point of change (see e.g. setTask, saveJobTemplate).
function persist() {}

async function unlockApp(event) {
  event.preventDefault();
  try {
    await api.auth.unlock(els.authPassword.value);
  } catch (error) {
    els.authError.textContent = "That password does not match the app settings.";
    return;
  }
  els.authError.textContent = "";
  await loadStateFromApi();
  els.authGate.classList.add("is-hidden");
  els.appShell.classList.remove("is-locked");
  els.authPassword.value = "";
  render();
}

async function lockApp() {
  try {
    await api.auth.lock();
  } catch (_error) {
    // best-effort
  }
  state = emptyState();
  els.authGate.classList.remove("is-hidden");
  els.appShell.classList.add("is-locked");
  els.authPassword.value = "";
  // Reset to the Today tab so the next login lands on the home screen,
  // not back on the settings page where the logout button lives.
  switchTab("today");
}

function selectedDate() {
  return parseDate(els.date.value);
}

function render() {
  const date = selectedDate();
  const jobs = getJobsForDate(date);
  const reportJobs = getAllJobsForDate(date);
  renderToday(date, jobs);
  renderNoteJobOptions(jobs);
  renderNotes(date);
  renderReport(date, reportJobs);
  renderTimesheet(date, reportJobs);
  renderTemplates();
  renderConfig();
}

function switchTab(tabName) {
  els.tabs.forEach((tab) => tab.classList.toggle("is-active", tab.dataset.tab === tabName));
  els.views.forEach((view) => view.classList.toggle("is-active", view.id === `${tabName}-view`));
  if (tabName === "report") renderReport(selectedDate(), getAllJobsForDate(selectedDate()));
  if (tabName === "timesheet") renderTimesheet(selectedDate(), getAllJobsForDate(selectedDate()));
}

function renderToday(date, jobs) {
  const dateLabel = date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
  els.todaySubtitle.textContent = dateLabel;

  const taskCount = jobs.reduce((sum, job) => sum + job.tasks.length, 0);
  const doneCount = jobs.reduce((sum, job) => sum + completedTaskCount(job), 0);
  const remainingCount = Math.max(taskCount - doneCount, 0);
  const noteCount = notesForDate(date).length;
  const specialCount = jobs.filter((job) => job.category === "special").length;
  els.summary.innerHTML = `
    <div class="summary-item"><strong>${jobs.length}</strong><span>Jobs showing</span></div>
    <div class="summary-item"><strong>${doneCount}/${remainingCount}</strong><span>Done / left</span></div>
    <div class="summary-item"><strong>${specialCount}</strong><span>Special jobs</span></div>
    <div class="summary-item"><strong>${noteCount}</strong><span>Notes today</span></div>
  `;

  renderPeriodProgress(date, jobs);

  els.jobList.innerHTML = "";
  if (!jobs.length) {
    const isPreEpoch = toDateInput(date) < APP_EPOCH;
    els.jobList.innerHTML = `<div class="empty-state">${
      isPreEpoch
        ? "Thomas used a different system before Monday 18 May 2026 — this app only shows work from then onwards."
        : "Nothing due for this day."
    }</div>`;
    return;
  }

  // Group by location so Thomas can see all the toilet jobs when he's at
  // the toilets, all the QEII jobs when he's at the park, etc. Locations
  // render in the canonical locationNames order (which mirrors how the
  // settings dropdown is ordered).
  const byArea = new Map();
  locationNames.forEach((name) => byArea.set(name, []));
  jobs.forEach((job) => {
    if (!byArea.has(job.area)) byArea.set(job.area, []);
    byArea.get(job.area).push(job);
  });

  byArea.forEach((groupJobs, area) => {
    if (!groupJobs.length) return;
    const doneInArea = groupJobs.filter(isJobComplete).length;
    const fullyDone = doneInArea === groupJobs.length;
    // Each location is an accordion (<details>). Default open so Thomas can
    // see everything at a glance; collapse the ones that are already done.
    // Toggle state persists per-render within the page via the open attribute.
    const group = document.createElement("details");
    group.className = `location-group${fullyDone ? " is-complete" : ""}`;
    if (!collapsedLocations.has(area) && !fullyDone) group.open = true;
    group.innerHTML = `
      <summary class="location-group-header">
        <span class="location-group-name">${escapeHtml(area)}</span>
        <span class="location-group-count">${doneInArea}/${groupJobs.length}</span>
        <span class="location-group-chevron" aria-hidden="true">›</span>
      </summary>
    `;
    group.addEventListener("toggle", () => {
      if (group.open) collapsedLocations.delete(area);
      else collapsedLocations.add(area);
    });
    const groupBody = document.createElement("div");
    groupBody.className = "location-group-body";
    groupJobs.forEach((job) => {
      try {
        groupBody.append(buildJobCard(job, date));
      } catch (error) {
        console.warn("Job could not be rendered.", job, error);
        const message = document.createElement("div");
        message.className = "empty-state";
        message.textContent = `Could not render ${job.title}: ${error.message}`;
        groupBody.append(message);
      }
    });
    group.append(groupBody);
    els.jobList.append(group);
  });
}

// Per-cadence "are we on track?" panel above the job list. Weekly jobs reset
// every Monday so progress tracking adds no value — we focus on the longer
// cadences where pacing matters. Each row shows two bars: jobs completed
// vs. time elapsed in the period. If time is meaningfully ahead of work,
// the row glows amber so Thomas can see he's falling behind on, say, the
// six-monthly jobs even though there's still time on the clock.
// Same order as cadencePriority — period length ascending.
const PROGRESS_CADENCES = ["monthly", "sixWeekly", "quarterly", "sixMonthly", "annual"];
// (No change needed — already in length order.)
const BEHIND_THRESHOLD = 15; // %

function renderPeriodProgress(date, allJobs) {
  if (!els.periodProgressMount) return;
  const today = parseDate(toDateInput(date));
  const rows = PROGRESS_CADENCES
    .map((cadence) => {
      const cadenceJobs = allJobs.filter((job) => job.cadence === cadence);
      if (!cadenceJobs.length) return null;
      const sample = cadenceJobs[0];
      const periodStart = parseDate(sample.period);
      const periodEnd = parseDate(sample.periodEnd);
      const totalDays = Math.round((periodEnd - periodStart) / 86400000) + 1;
      const elapsedDays = Math.max(0, Math.min(totalDays, Math.round((today - periodStart) / 86400000) + 1));
      const daysLeft = Math.max(0, totalDays - elapsedDays);
      const done = cadenceJobs.filter(isJobComplete).length;
      const pctDone = Math.round((done / cadenceJobs.length) * 100);
      const pctTime = Math.round((elapsedDays / totalDays) * 100);
      return {
        cadence,
        label: cadenceLabel(cadence),
        done,
        total: cadenceJobs.length,
        pctDone,
        pctTime,
        daysLeft,
        behind: pctTime - pctDone > BEHIND_THRESHOLD,
      };
    })
    .filter(Boolean);

  if (!rows.length) {
    els.periodProgressMount.innerHTML = "";
    return;
  }

  const anyBehind = rows.some((row) => row.behind);
  els.periodProgressMount.innerHTML = `
    <details class="period-progress${anyBehind ? " has-behind" : ""}" ${anyBehind ? "open" : ""}>
      <summary class="period-progress-summary">
        <span class="period-progress-title">Period progress</span>
        <span class="period-progress-meta">${anyBehind ? `${rows.filter((r) => r.behind).length} cadence${rows.filter((r) => r.behind).length === 1 ? "" : "s"} behind` : "On track"}</span>
        <span class="period-progress-chevron" aria-hidden="true">›</span>
      </summary>
      <div class="period-progress-body">
        ${rows.map((row) => `
          <div class="period-progress-row${row.behind ? " is-behind" : ""}">
            <div class="period-progress-head">
              <span class="period-progress-cadence">${escapeHtml(row.label)}</span>
              <span class="period-progress-counts">${row.done} of ${row.total}</span>
            </div>
            <div class="period-progress-bars">
              <div class="period-progress-bar"><div class="period-progress-fill done" style="width:${row.pctDone}%"></div></div>
              <div class="period-progress-bar"><div class="period-progress-fill time" style="width:${row.pctTime}%"></div></div>
            </div>
            <div class="period-progress-foot">${row.pctTime}% of period elapsed · ${row.daysLeft} day${row.daysLeft === 1 ? "" : "s"} left</div>
          </div>
        `).join("")}
      </div>
    </details>
  `;
}

function buildJobCard(job, date) {
  const node = els.jobTemplate.content.firstElementChild.cloneNode(true);
  const done = isJobComplete(job);
  node.classList.add(jobCardClass(job));
  node.classList.toggle("is-shared", job.shared);
  node.querySelector(".job-meta").textContent = cadenceLabel(job.cadence);
  node.querySelector(".job-location").textContent = job.area;
  node.querySelector("h3").textContent = job.title;
  node.querySelector(".job-description").textContent = job.description;
  const badge = node.querySelector(".status-badge");
  badge.textContent = done ? "Done" : job.category === "special" ? `Special · ${completedTaskCount(job)}/${job.tasks.length}` : `${completedTaskCount(job)}/${job.tasks.length}`;
  badge.classList.toggle("done", done);
  badge.classList.toggle("special", job.category === "special" && !done);
  const header = node.querySelector(".job-card-header");
  const detail = node.querySelector(".job-detail");
  if (expandedJobs.has(job.occurrenceKey)) {
    header.setAttribute("aria-expanded", "true");
    detail.classList.remove("is-hidden");
  }
  header.addEventListener("click", () => {
    const expanded = header.getAttribute("aria-expanded") === "true";
    header.setAttribute("aria-expanded", String(!expanded));
    detail.classList.toggle("is-hidden", expanded);
    if (expanded) {
      expandedJobs.delete(job.occurrenceKey);
    } else {
      expandedJobs.add(job.occurrenceKey);
    }
  });

  const taskList = node.querySelector(".task-list");
  job.tasks.forEach((task, index) => {
    if (getCompletion(job).tasks[index] === true) return;
    const row = document.createElement("div");
    row.className = "task-row";
    row.innerHTML = `
      <label class="task-check">
        <input type="checkbox">
        <span>${escapeHtml(task)}</span>
      </label>
      <button class="icon-button task-note-toggle" type="button" aria-label="Add note for ${escapeHtml(task)}" title="Add note">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3Z"></path>
          <path d="M5 11a7 7 0 0 0 14 0"></path>
          <path d="M12 18v3"></path>
          <path d="M8 21h8"></path>
        </svg>
      </button>
      <div class="task-note-panel is-hidden">
        <textarea rows="2" placeholder="Note for ${escapeHtml(task)}"></textarea>
        <div class="button-row">
          <button class="voice-button task-dictate" type="button">Speak</button>
          <button class="small-button task-note-save" type="button">Save note</button>
        </div>
      </div>
    `;
    row.querySelector("input").addEventListener("change", (event) => {
      setTask(job, index, event.target.checked, date);
    });
    const panel = row.querySelector(".task-note-panel");
    const textarea = row.querySelector("textarea");
    row.querySelector(".task-note-toggle").addEventListener("click", () => {
      panel.classList.toggle("is-hidden");
      if (!panel.classList.contains("is-hidden")) textarea.focus();
    });
    row.querySelector(".task-dictate").addEventListener("click", () => dictateInto(textarea));
    row.querySelector(".task-note-save").addEventListener("click", () => {
      const text = textarea.value.trim();
      if (!text) return;
      addNote({
        date: toDateInput(date),
        jobId: job.id,
        jobTitle: job.title,
        taskIndex: index,
        taskText: task,
        location: job.area,
        priority: inferPriority(text),
        text,
      });
      textarea.value = "";
      panel.classList.add("is-hidden");
    });
    taskList.append(row);
  });

  const noteBox = node.querySelector(".quick-note textarea");
  node.querySelector(".dictate-job-note").addEventListener("click", () => dictateInto(noteBox));
  node.querySelector(".save-job-note").addEventListener("click", () => {
    const text = noteBox.value.trim();
    if (!text) return;
    addNote({
      date: toDateInput(date),
      jobId: job.id,
      jobTitle: job.title,
      location: job.area,
      priority: inferPriority(text),
      text,
    });
    noteBox.value = "";
  });
  return node;
}

function renderNoteJobOptions(jobs) {
  els.noteJob.innerHTML = '<option value="">General note</option>';
  jobs.forEach((job) => {
    const option = document.createElement("option");
    option.value = job.id;
    option.textContent = job.title;
    els.noteJob.append(option);
  });
}

function renderNotes(date) {
  els.notesFilters.forEach((button) => {
    button.classList.toggle("is-active", button.dataset.notesFilter === notesFilter);
  });
  const notes = notesForFilter(date, notesFilter);
  els.notesList.innerHTML = "";
  if (!notes.length) {
    els.notesList.innerHTML = `<div class="empty-state">No repair notes recorded for ${notesFilterLabel(notesFilter).toLowerCase()} yet.</div>`;
    return;
  }
  notes.forEach((note) => {
    const card = document.createElement("article");
    card.className = "note-card";
    card.innerHTML = `
      <header>
        <div>
          <h3>${escapeHtml(note.location || note.jobTitle || "General note")}</h3>
          <p>${escapeHtml(formatNoteDate(note.date))} · ${escapeHtml(noteLineContext(note))} · ${escapeHtml(note.time)}</p>
        </div>
      </header>
      <p>${escapeHtml(note.text)}</p>
      <div class="note-actions">
        <button class="small-button edit-note" type="button">Edit</button>
        <button class="small-button delete-note" type="button">Delete</button>
      </div>
    `;
    card.querySelector(".edit-note").addEventListener("click", () => startNoteEdit(note.id));
    card.querySelector(".delete-note").addEventListener("click", () => {
      state.notes = state.notes.filter((item) => item.id !== note.id);
      api.notes.remove(note.id).catch((error) => console.error("Failed to delete note", error));
      render();
    });
    els.notesList.append(card);
  });
}

function renderReport(date, jobs) {
  const notes = notesForDate(date);
  const tasksDoneToday = jobs.reduce((sum, job) => sum + completedTaskCountOnDate(job, date), 0);
  const jobsWorkedToday = jobs.filter((job) => completedTaskCountOnDate(job, date) > 0);
  const outstandingJobs = jobs.filter((job) => !isJobComplete(job));
  const monthlyProgress = progressForCadence(jobs, "monthly");
  const sixWeeklyProgress = progressForCadence(jobs, "sixWeekly");
  const quarterlyProgress = progressForCadence(jobs, "quarterly");
  const totalMinutes = timesheetTotal(date);
  const plannedMinutes = plannedMinutesForDate(date);
  const reportDate = date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
  const lines = [
    `Thomas update - ${reportDate}`,
    "",
    `${jobsWorkedToday.length} jobs worked on, ${tasksDoneToday} tasks ticked today. ${outstandingJobs.length} jobs still open. ${notes.length} notes.`,
    `Monthly ${monthlyProgress.done}/${monthlyProgress.total}. Six-weekly ${sixWeeklyProgress.done}/${sixWeeklyProgress.total}. Quarterly ${quarterlyProgress.done}/${quarterlyProgress.total}.`,
    `Timesheet ${formatMinutes(totalMinutes)} / ${formatMinutes(plannedMinutes)} paid time.`,
    "",
    "PDF job list attached."
  ];
  els.report.value = lines.join("\n");
}

async function sendReportEmail(type) {
  const payload = reportEmailPayload(type, selectedDate());
  if (!payload.to.length) {
    els.emailStatus.textContent = `Add ${type} report recipients in Job settings before sending.`;
    return;
  }
  if (!emailConfigured) {
    els.emailStatus.textContent = "Email isn't configured on this environment yet — RESEND_API_KEY needs to be set.";
    return;
  }
  els.emailStatus.textContent = `Sending ${type} report to ${payload.to.join(", ")}...`;
  try {
    await api.sendReport({ to: payload.to, subject: payload.subject, text: payload.text });
    els.emailStatus.textContent = `${capitalize(type)} report sent.`;
  } catch (error) {
    els.emailStatus.textContent = `Failed to send ${type} report: ${error.message}`;
    console.error("sendReport failed", error);
  }
}

function reportEmailPayload(type, date) {
  const email = state.config.email;
  const jobs = getAllJobsForDate(date);
  const notes = notesForDate(date);
  return {
    provider: "resend",
    endpoint: email.endpoint || "/api/send-report",
    type,
    from: email.from,
    to: recipientsForReportType(type),
    subject: reportEmailSubject(type, date),
    date: toDateInput(date),
    text: els.report.value,
    summary: {
      timesheetMinutes: timesheetTotal(date),
      plannedMinutes: plannedMinutesForDate(date),
      weeklyTimesheetMinutes: weeklyTimesheetTotal(date),
      weeklyPlannedMinutes: weeklyPlannedMinutes(date),
      jobsWorkedToday: jobs.filter((job) => completedTaskCountOnDate(job, date) > 0).map((job) => job.title),
      notes: notes.map((note) => ({ location: note.location, jobTitle: note.jobTitle, text: note.text, priority: note.priority }))
    }
  };
}

function recipientsForReportType(type) {
  const email = state.config.email;
  if (type === "weekly") return parseRecipients(email.weeklyRecipients);
  if (type === "timed") return parseRecipients(email.timedRecipients);
  return parseRecipients(email.dailyRecipients);
}

function parseRecipients(value) {
  return String(value || "")
    .split(/[\n,;]/)
    .map((recipient) => recipient.trim())
    .filter(Boolean);
}

function capitalize(value) {
  return `${value}`.charAt(0).toUpperCase() + `${value}`.slice(1);
}

function reportEmailSubject(type, date) {
  const dateLabel = date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
  if (type === "weekly") return `Thomas weekly maintenance report - week of ${formatShortDate(parseDate(weekStartDate(date)))}`;
  if (type === "timed") return `Thomas timed maintenance update - ${dateLabel} ${state.config.email.timedSendTime}`;
  return `Thomas daily maintenance report - ${dateLabel}`;
}

function renderTimesheet(date, jobs) {
  const total = timesheetTotal(date);
  const planned = plannedMinutesForDate(date);
  const remaining = planned - total;
  const status = timesheetStatus(remaining);
  els.timesheetPanel.innerHTML = `
    <div class="section-heading timesheet-heading">
      <div>
        <h2 id="timesheet-title">Timesheet</h2>
        <p>Allocate ${formatMinutes(planned)} of paid time today. Locations appear here automatically when you tick off a job in the <strong>Today</strong> tab; add hours below.</p>
      </div>
      <span class="time-total ${status.className}">${formatMinutes(total)} / ${formatMinutes(planned)}</span>
    </div>
  `;

  const locations = timesheetLocations(date, jobs);
  if (!locations.length) {
    els.timesheetPanel.insertAdjacentHTML("beforeend", '<div class="empty-state">No locations on the timesheet yet. Tick a task off in the Today tab and its location appears here for you to log hours against.</div>');
  } else {
    const list = document.createElement("div");
    list.className = "timesheet-list";
    locations.forEach((location) => {
      const row = document.createElement("label");
      row.className = "time-row";
      row.innerHTML = `
        <span>${escapeHtml(location)}</span>
        <input type="text" inputmode="decimal" value="${formatTimeEntryValue(timeEntry(date, location))}" placeholder="30m or 2h">
      `;
      row.querySelector("input").addEventListener("input", (event) => {
        const minutes = parseTimeEntry(event.target.value);
        if (minutes === null) return;
        setTimeEntry(date, location, minutes, false);
        refreshTimesheetTotals(date);
        renderReport(date, getAllJobsForDate(date));
      });
      row.querySelector("input").addEventListener("blur", (event) => {
        event.target.value = formatTimeEntryValue(timeEntry(date, location));
      });
      list.append(row);
    });
    els.timesheetPanel.append(list);
  }

  const hint = document.createElement("p");
  hint.className = `hint time-hint ${status.className}`;
  hint.textContent = status.message;
  els.timesheetPanel.append(hint);

  const help = document.createElement("p");
  help.className = "hint time-help";
  help.textContent = "Use 30 for minutes, 2 for hours, or type 2h 30 / 2:30.";
  els.timesheetPanel.append(help);

  renderWeeklyTimesheet(date);
  renderExtraTimesheetForm(date);
}

function renderExtraTimesheetForm(date) {
  const form = document.createElement("form");
  form.className = "extra-time-form";
  form.innerHTML = `
    <div>
      <h3>Add extra time</h3>
      <p class="hint">For meetings, events, weekend work, or timekeeping entries outside the planned job list.</p>
    </div>
    <div class="extra-time-grid">
      <label>
        <span>Date</span>
        <input class="extra-time-date" type="date" value="${toDateInput(date)}">
      </label>
      <label>
        <span>Location</span>
        <select class="extra-time-location">
          ${locationNames.map((location) => `<option>${escapeHtml(location)}</option>`).join("")}
        </select>
      </label>
      <label>
        <span>Time</span>
        <input class="extra-time-duration" type="text" inputmode="decimal" placeholder="30m or 2h">
      </label>
    </div>
    <label>
      <span>What was the extra work?</span>
      <input class="extra-time-description" type="text" placeholder="Meeting at the office">
    </label>
    <button class="small-button" type="submit">Add time</button>
  `;
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const entryDate = form.querySelector(".extra-time-date").value;
    const location = form.querySelector(".extra-time-location").value;
    const duration = form.querySelector(".extra-time-duration").value;
    const description = form.querySelector(".extra-time-description").value.trim();
    const minutes = parseTimeEntry(duration);
    if (!entryDate || !location || minutes === null || minutes <= 0 || !description) return;
    addTimeEntry(parseDate(entryDate), location, minutes, description);
  });
  els.timesheetPanel.append(form);
}

function renderWeeklyTimesheet(date) {
  const start = weekStartDate(date);
  const end = addDaysToDate(start, 6);
  els.timesheetPanel.insertAdjacentHTML("beforeend", `
    <div class="section-heading weekly-timesheet-heading">
      <div>
        <h2>This week</h2>
        <p>Monday to Sunday, ${formatShortDate(parseDate(start))} to ${formatShortDate(parseDate(end))}.</p>
      </div>
      <span class="time-total">${formatMinutes(weeklyTimesheetTotal(date))} / ${formatMinutes(weeklyPlannedMinutes(date))}</span>
    </div>
  `);

  els.timesheetPanel.insertAdjacentHTML("beforeend", `
    <p class="weekly-timesheet-hint">Swipe the grid sideways to see all locations →</p>
  `);
  const table = document.createElement("div");
  table.className = "timesheet-table timesheet-grid";
  const days = weekDates(date);
  table.append(timesheetGridRow(["Day", ...locationNames, "Total"], true));
  table.append(timesheetGridRow(["Date", ...locationNames.map(() => "HRS"), "HRS"], true));
  days.forEach((day) => {
    const values = locationNames.map((location) => timeEntry(parseDate(day), location));
    table.insertAdjacentHTML("beforeend", `
      ${timesheetGridRowHtml([
        parseDate(day).toLocaleDateString("en-GB", { weekday: "long" }),
        ...values.map(formatTimesheetHours),
        formatTimesheetHours(values.reduce((sum, value) => sum + value, 0))
      ])}
    `);
  });
  table.append(timesheetGridRow(["Total HRS", ...locationNames.map((location) => formatTimesheetHours(weeklyLocationTotal(date, location))), formatTimesheetHours(weeklyTimesheetTotal(date))], true, "timesheet-table-total"));
  els.timesheetPanel.append(table);
}

function timesheetGridRow(cells, header = false, extraClass = "") {
  const row = document.createElement("div");
  row.className = `timesheet-table-row ${header ? "timesheet-table-head" : ""} ${extraClass}`.trim();
  row.innerHTML = cells.map((cell) => `<span>${escapeHtml(cell)}</span>`).join("");
  return row;
}

function timesheetGridRowHtml(cells) {
  return `<div class="timesheet-table-row">${cells.map((cell) => `<span>${escapeHtml(cell)}</span>`).join("")}</div>`;
}

function renderTemplates() {
  els.templateList.innerHTML = "";
  const visibleTemplates = state.templates.filter((template) => !template.archived);
  const templates = visibleTemplates.length ? visibleTemplates : state.templates;
  if (!templates.length) {
    const empty = document.createElement("div");
    empty.className = "empty-state";
    empty.innerHTML = `<p>No jobs in the database yet. Run <code>npm run db:seed</code> on the server to load the bundled maintenance schedule.</p>`;
    els.templateList.append(empty);
    return;
  }
  templates.forEach((template) => {
    const card = document.createElement("article");
    card.className = `template-card${template.category === "special" ? " special-template" : ""}`;
    const schedule = scheduleLabel(template);
    const details = document.createElement("details");
    details.innerHTML = `
      <summary>
        <div class="template-summary">
          <h3>${escapeHtml(template.title)}</h3>
          <p>${escapeHtml(template.area)} · ${escapeHtml(schedule)}</p>
        </div>
        <span class="priority${template.category === "special" ? " special" : ""}">${template.archived ? "Archived" : template.shared ? "Shared" : cadenceLabel(template.cadence)}</span>
      </summary>
      <div class="template-body">
        ${template.description ? `<p>${escapeHtml(template.description)}</p>` : ""}
        <p>${escapeHtml(template.tasks.join(", "))}</p>
      </div>
    `;
    const actions = document.createElement("div");
    actions.className = "template-actions";
    const edit = document.createElement("button");
    edit.className = "small-button";
    edit.type = "button";
    edit.textContent = "Edit";
    edit.addEventListener("click", () => startJobEdit(template.id));
    actions.append(edit);
    if (template.userCreated) {
      const remove = document.createElement("button");
      remove.className = "small-button";
      remove.type = "button";
      remove.textContent = "Remove";
      remove.addEventListener("click", () => {
        state.templates = state.templates.filter((item) => item.id !== template.id);
        api.templates.remove(template.id).catch((error) => {
          console.error("Failed to delete template", error);
        });
        render();
      });
      actions.append(remove);
    }
    details.querySelector(".template-body").append(actions);
    card.append(details);
    els.templateList.append(card);
  });
}

function renderConfig() {
  const config = state.config;
  // Password and endpoint are now controlled server-side (env vars on
  // each Railway environment). Show them read-only with a hint.
  if (els.appPasswordSetting) {
    els.appPasswordSetting.value = "";
    els.appPasswordSetting.placeholder = "Managed server-side (APP_PASSWORD env var)";
    els.appPasswordSetting.disabled = true;
  }
  els.resendFrom.value = config.email.from;
  els.dailyRecipients.value = config.email.dailyRecipients;
  els.weeklyRecipients.value = config.email.weeklyRecipients;
  els.timedRecipients.value = config.email.timedRecipients;
  els.timedSendTime.value = config.email.timedSendTime;
  els.workDayChecks.forEach((input) => {
    const day = input.dataset.workDay;
    input.checked = Boolean(config.workingDays[day]?.enabled);
  });
  els.workDayHours.forEach((input) => {
    const day = input.dataset.workHours;
    input.value = formatTimeEntryValue(config.workingDays[day]?.minutes || 0);
  });
  refreshWorkingWeekSummary();
}

function saveConfig(event) {
  event.preventDefault();
  const workingDays = {};
  els.workDayChecks.forEach((input) => {
    const day = input.dataset.workDay;
    const hoursInput = document.querySelector(`[data-work-hours="${day}"]`);
    const minutes = parseTimeEntry(hoursInput.value) || 0;
    workingDays[day] = {
      enabled: input.checked,
      minutes: input.checked ? minutes : 0
    };
  });
  state.config = {
    ...state.config,
    workingDays,
    email: {
      ...state.config.email,
      from: els.resendFrom.value.trim() || state.config.email.from,
      dailyRecipients: els.dailyRecipients.value.trim(),
      weeklyRecipients: els.weeklyRecipients.value.trim(),
      timedRecipients: els.timedRecipients.value.trim(),
      timedSendTime: els.timedSendTime.value || "17:00",
    },
  };
  api.config.update({
    workingDays,
    emailFrom: state.config.email.from,
    dailyRecipients: state.config.email.dailyRecipients,
    weeklyRecipients: state.config.email.weeklyRecipients,
    timedRecipients: state.config.email.timedRecipients,
    timedSendTime: state.config.email.timedSendTime,
  }).then(() => {
    els.emailStatus.textContent = "Settings saved.";
  }).catch((error) => {
    els.emailStatus.textContent = `Failed to save settings: ${error.message}`;
  });
  render();
}

function refreshWorkingWeekSummary() {
  const total = [...els.workDayChecks].reduce((sum, input) => {
    if (!input.checked) return sum;
    const hoursInput = document.querySelector(`[data-work-hours="${input.dataset.workDay}"]`);
    return sum + (parseTimeEntry(hoursInput.value) || 0);
  }, 0);
  els.weeklyHoursSummary.textContent = `Paid weekly total: ${formatMinutes(total)}. Timesheet overtime is calculated against these settings.`;
}

function saveJobTemplate(event) {
  event.preventDefault();
  let tasks = els.jobTasks.value
    .split("\n")
    .map((task) => task.trim())
    .filter(Boolean);
  const title = els.jobTitle.value.trim();
  if (!tasks.length && title) tasks = [title];
  if (!tasks.length) return;

  const template = {
    id: `job-${Date.now()}`,
    title,
    area: els.jobArea.value.trim(),
    category: els.jobCadence.value === "once" ? "special" : els.jobKind.value,
    cadence: els.jobCadence.value,
    timing: els.jobCadence.value === "once" ? "fixed" : els.jobTiming.value,
    description: els.jobDescription.value.trim() || "Added by Thomas.",
    tasks,
    shared: els.jobShared.checked,
    userCreated: true
  };

  const existing = editingTemplateId
    ? state.templates.find((item) => item.id === editingTemplateId)
    : null;
  if (existing) {
    template.id = `${existing.id}-v${Date.now()}`;
    template.userCreated = existing.userCreated;
    template.replacesId = existing.id;
  }

  if (template.cadence === "weekly" || template.cadence === "sixWeekly") {
    template.day = Number(els.jobDay.value);
  } else if (["monthly", "quarterly", "sixMonthly", "annual"].includes(template.cadence)) {
    template.dayOfMonth = clamp(Number(els.jobMonthDay.value), 1, 31);
  } else {
    template.dueDate = els.jobOnceDate.value || els.date.value;
  }

  if (existing) {
    const archived = {
      ...existing,
      archived: true,
      archivedAt: toDateInput(selectedDate())
    };
    state.templates = state.templates.map((item) => item.id === existing.id ? archived : item);
    state.templates.unshift(template);
    Promise.all([
      api.templates.upsert(serialiseTemplate(archived)),
      api.templates.upsert(serialiseTemplate(template)),
    ]).catch((error) => console.error("Failed to save job edit", error));
  } else {
    state.templates.unshift(template);
    api.templates.upsert(serialiseTemplate(template)).catch((error) => {
      console.error("Failed to save new job", error);
    });
  }
  resetJobForm();
  render();
}

function serialiseTemplate(template) {
  // Drop client-only fields and ensure expected types for the API.
  return {
    id: template.id,
    title: template.title,
    area: template.area,
    cadence: template.cadence,
    category: template.category || "routine",
    timing: template.timing || "flex",
    day: typeof template.day === "number" ? template.day : null,
    dayOfMonth: typeof template.dayOfMonth === "number" ? template.dayOfMonth : null,
    dueDate: template.dueDate || null,
    shared: Boolean(template.shared),
    description: template.description || "",
    tasks: Array.isArray(template.tasks) ? template.tasks : [],
    userCreated: Boolean(template.userCreated),
    archived: Boolean(template.archived),
    archivedAt: template.archivedAt || "",
    replacesId: template.replacesId || "",
  };
}

function startJobEdit(templateId) {
  const template = state.templates.find((item) => item.id === templateId);
  if (!template) return;
  editingTemplateId = template.id;
  els.jobKind.value = template.category || "routine";
  els.jobCadence.value = template.cadence;
  els.jobTitle.value = template.title;
  els.jobArea.value = template.area;
  els.jobDescription.value = template.description || "";
  els.jobShared.checked = Boolean(template.shared);
  els.jobTiming.value = template.timing || "fixed";
  els.jobDay.value = String(template.day || 2);
  els.jobMonthDay.value = template.dayOfMonth || 1;
  els.jobOnceDate.value = template.dueDate || els.date.value;
  els.jobTasks.value = (template.tasks || []).join("\n");
  els.jobSubmit.textContent = "Save job";
  els.cancelJobEdit.classList.remove("is-hidden");
  renderScheduleControls();
  switchTab("jobs");
  els.jobTitle.focus();
}

function cancelJobEdit() {
  resetJobForm();
  render();
}

function resetJobForm() {
  editingTemplateId = "";
  els.jobForm.reset();
  els.jobOnceDate.value = els.date.value;
  els.jobSubmit.textContent = "Add job";
  els.cancelJobEdit.classList.add("is-hidden");
  renderScheduleControls();
}

function renderScheduleControls() {
  const cadence = els.jobCadence.value;
  const timing = els.jobTiming.value;
  els.timingField.classList.toggle("is-hidden", cadence === "once");
  els.weeklyDayField.classList.toggle("is-hidden", !["weekly", "sixWeekly"].includes(cadence) || timing !== "fixed");
  els.periodDayField.classList.toggle("is-hidden", !["monthly", "quarterly", "sixMonthly", "annual"].includes(cadence) || timing !== "fixed");
  els.onceDateField.classList.toggle("is-hidden", cadence !== "once");
}

function syncOneOffDefaults() {
  if (els.jobCadence.value !== "once") return;
  els.jobKind.value = "special";
  if (!els.jobOnceDate.value) els.jobOnceDate.value = els.date.value;
}

function saveManualNote(event) {
  event.preventDefault();
  const job = getJobsForDate(selectedDate()).find((item) => item.id === els.noteJob.value);
  const text = els.noteText.value.trim();
  if (!text) return;
  if (editingNoteId) {
    const patch = {
      date: els.date.value,
      jobId: job?.id || "",
      jobTitle: job?.title || "",
      location: els.noteLocation.value.trim(),
      priority: els.notePriority.value,
      text,
    };
    state.notes = state.notes.map((note) => note.id === editingNoteId ? { ...note, ...patch } : note);
    api.notes.update(editingNoteId, patch).catch((error) => {
      console.error("Failed to update note", error);
    });
    resetNoteForm();
    render();
    return;
  }
  addNote({
    date: els.date.value,
    jobId: job?.id || "",
    jobTitle: job?.title || "",
    location: els.noteLocation.value.trim(),
    priority: els.notePriority.value,
    text
  });
  resetNoteForm();
}

function startNoteEdit(noteId) {
  const note = state.notes.find((item) => item.id === noteId);
  if (!note) return;
  editingNoteId = note.id;
  els.date.value = note.date || els.date.value;
  renderNoteJobOptions(getJobsForDate(selectedDate()));
  els.noteLocation.value = note.location || "";
  els.noteText.value = note.text || "";
  els.notePriority.value = note.priority || "Routine";
  els.noteJob.value = note.jobId || "";
  els.noteSubmit.textContent = "Save note";
  els.cancelNoteEdit.classList.remove("is-hidden");
  els.noteText.focus();
}

function cancelNoteEdit() {
  resetNoteForm();
}

function resetNoteForm() {
  editingNoteId = "";
  els.noteForm.reset();
  els.noteSubmit.textContent = "Add note";
  els.cancelNoteEdit.classList.add("is-hidden");
}

function addNote(note) {
  const now = new Date();
  const id = (crypto.randomUUID?.() ?? `note-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const row = {
    id,
    time: now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
    ...note,
  };
  state.notes.unshift(row);
  api.notes.create({
    id,
    date: row.date,
    location: row.location,
    text: row.text,
    priority: row.priority,
    jobId: row.jobId,
    jobTitle: row.jobTitle,
    taskText: row.taskText || "",
  }).catch((error) => console.error("Failed to save note", error));
  render();
}

function getJobsForDate(date) {
  return getAllJobsForDate(date).filter((job) => !isJobComplete(job));
}

function getAllJobsForDate(date) {
  const jobs = state.templates
    .filter((template) => templateShouldAppear(template, date))
    .flatMap((template) => occurrencesForTemplate(template, date)
      .map((occurrence) => withOccurrence(template, occurrence, labelFor(template, occurrence, date))));

  return jobs.sort((a, b) => {
    const priority = cadencePriority(a) - cadencePriority(b);
    if (priority !== 0) return priority;
    return a.title.localeCompare(b.title);
  });
}

function templateShouldAppear(template, date) {
  if (!template.archived) return true;
  // An archived template only re-appears if its current period has tasks
  // already ticked off (Thomas is still working through it).
  return occurrencesForTemplate(template, date).some((occurrence) => {
    const completion = state.completions[occurrence.key];
    return completion && Object.values(completion.tasks || {}).some(Boolean);
  });
}

function occurrencesForTemplate(template, date) {
  const occurrence = occurrenceFor(template, date);
  return occurrence ? [occurrence] : [];
}

// One occurrence per template per selected date — the period that contains
// the date. No past carry-over (assumed done) and no future leak. Periods
// re-anchor to 1 January each year for sixWeekly / quarterly / etc, so the
// cycle restarts cleanly at year boundaries.
function occurrenceFor(template, date) {
  const selected = toDateInput(date);
  if (selected < APP_EPOCH) return null;

  const period = periodFor(template, date);
  if (!period) return null;
  // A period that ended before the app's epoch was a pre-app period —
  // its work happened in the old system, not here.
  if (period.end < APP_EPOCH) return null;

  return {
    key: `${template.id}:${period.start}`,
    period: period.start,
    periodEnd: period.end,
    dueDate: dueDateFor(template, period),
  };
}

function periodFor(template, date) {
  const selected = toDateInput(date);
  if (template.cadence === "weekly") {
    const start = weekStartDate(date);
    return { start, end: addPeriodStart(start, 6) };
  }
  if (template.cadence === "sixWeekly") {
    return sixWeeklyPeriod(date);
  }
  if (template.cadence === "monthly") {
    const month = selected.slice(0, 7);
    const end = `${month}-${String(daysInMonth(date)).padStart(2, "0")}`;
    return { start: `${month}-01`, end };
  }
  if (template.cadence === "quarterly") {
    const start = quarterStartDate(date);
    const end = addDaysToDate(addMonthsToDate(start, 3), -1);
    return { start, end };
  }
  if (template.cadence === "sixMonthly") {
    const start = sixMonthStartDate(date);
    const end = addDaysToDate(addMonthsToDate(start, 6), -1);
    return { start, end };
  }
  if (template.cadence === "annual") {
    const year = date.getFullYear();
    return { start: `${year}-01-01`, end: `${year}-12-31` };
  }
  if (template.cadence === "once") {
    const due = template.dueDate || selected;
    if (selected < due) return null;
    return { start: due, end: due };
  }
  return null;
}

function dueDateFor(template, period) {
  if (template.cadence === "once") return period.start;
  if (template.timing !== "fixed") return period.start;
  if (template.cadence === "weekly" || template.cadence === "sixWeekly") {
    // template.day is 0-6 (Sunday = 0, Monday = 1 in our convention).
    // period.start is a Monday — the days offset from start.
    const offset = ((template.day || 1) - 1 + 7) % 7;
    return toDateInput(addDays(parseDate(period.start), offset));
  }
  // monthly / quarterly / sixMonthly / annual: dayOfMonth into the period
  const day = Math.min(template.dayOfMonth || 1, 31) - 1;
  const due = toDateInput(addDays(parseDate(period.start), day));
  // Clamp to within the period (handles month-end edge cases).
  return due > period.end ? period.end : due;
}

// Six-weekly periods anchor to 1 January of the date's year and run in
// 42-day blocks. The final block of the year is truncated at 31 Dec so it
// doesn't bleed into next year — January 1st always restarts the cycle.
function sixWeeklyPeriod(date) {
  const year = date.getFullYear();
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  const dayIndex = Math.floor((parseDate(toDateInput(date)) - parseDate(yearStart)) / (24 * 60 * 60 * 1000));
  const blockIndex = Math.floor(dayIndex / 42);
  const start = addPeriodStart(yearStart, blockIndex * 42);
  const naturalEnd = addPeriodStart(start, 41);
  const end = naturalEnd > yearEnd ? yearEnd : naturalEnd;
  return { start, end };
}

function withOccurrence(template, occurrence, label) {
  return {
    ...template,
    occurrenceKey: occurrence.key,
    dueDate: occurrence.dueDate,
    period: occurrence.period,
    periodEnd: occurrence.periodEnd,
    label
  };
}

function labelFor(template, occurrence, date) {
  if (template.cadence === "once" && occurrence.dueDate < toDateInput(date)) return `Date-specific job from ${formatShortDate(parseDate(occurrence.dueDate))}`;
  if (template.cadence === "once") return `Date-specific job for ${formatShortDate(parseDate(occurrence.dueDate))}`;
  if (template.cadence === "weekly") return "To complete this week";
  if (template.cadence === "monthly") return "To complete this month";
  if (template.cadence === "sixWeekly") return "To complete this 6-week period";
  if (template.cadence === "sixMonthly") return "To complete this 6-month period";
  if (template.cadence === "quarterly") return "To complete this quarter";
  if (template.cadence === "annual") return "To complete this year";
  return "Scheduled job";
}

function getCompletion(job) {
  if (!state.completions[job.occurrenceKey]) {
    state.completions[job.occurrenceKey] = { tasks: {}, completedAt: {}, templateId: job.id, period: job.period };
  }
  if (!state.completions[job.occurrenceKey].completedAt) state.completions[job.occurrenceKey].completedAt = {};
  return state.completions[job.occurrenceKey];
}

function setTask(job, index, checked, date = selectedDate()) {
  expandedJobs.add(job.occurrenceKey);
  const completion = getCompletion(job);
  completion.tasks[index] = checked;
  if (checked) {
    completion.completedAt[index] = toDateInput(date);
  } else {
    delete completion.completedAt[index];
  }
  if (checked) addWorkedLocation(date, job.area);
  const payload = {
    templateId: job.id,
    period: job.period,
    tasks: completionTasksForApi(completion),
  };
  api.completions.upsert(job.occurrenceKey, payload).catch((error) => {
    console.error("Failed to save task state", error);
  });
  render();
}

function completionTasksForApi(completion) {
  const out = {};
  Object.entries(completion.tasks).forEach(([index, checked]) => {
    out[index] = { checked: Boolean(checked), date: completion.completedAt?.[index] || "" };
  });
  return out;
}

function completedTaskCount(job) {
  return job.tasks.filter((_, index) => getCompletion(job).tasks[index] === true).length;
}

function remainingTaskCount(job) {
  return Math.max(job.tasks.length - completedTaskCount(job), 0);
}

function isJobComplete(job) {
  return completedTaskCount(job) === job.tasks.length;
}

function completedTaskCountOnDate(job, date) {
  const key = toDateInput(date);
  const completion = getCompletion(job);
  return job.tasks.filter((_, index) => completion.completedAt[index] === key).length;
}

function progressForCadence(jobs, cadence) {
  const relevant = jobs.filter((job) => job.cadence === cadence);
  return {
    done: relevant.filter(isJobComplete).length,
    total: relevant.length
  };
}

function notesForDate(date) {
  const key = toDateInput(date);
  return state.notes.filter((note) => note.date === key);
}

function notesForFilter(date, filter) {
  const selectedKey = toDateInput(date);
  if (filter === "today") return notesForDate(date);
  if (filter === "week") {
    const start = weekStartDate(date);
    const end = addDaysToDate(start, 6);
    return state.notes.filter((note) => note.date >= start && note.date <= end);
  }
  if (filter === "month") {
    const month = selectedKey.slice(0, 7);
    return state.notes.filter((note) => note.date?.slice(0, 7) === month);
  }
  return notesForDate(date);
}

function notesFilterLabel(filter) {
  return filter === "week" ? "This week" : filter === "month" ? "This month" : "Today";
}

function formatNoteDate(value) {
  if (!value) return "No date";
  return formatShortDate(parseDate(value));
}

function formatTimesheetDate(value) {
  if (!value) return "No date";
  const date = parseDate(value);
  return date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

function addWorkedLocation(date, location) {
  const key = toDateInput(date);
  if (!state.workLocations[key]) state.workLocations[key] = {};
  if (state.workLocations[key][location]) return;
  state.workLocations[key][location] = true;
  api.workLocations.add(key, location).catch((error) => {
    console.error("Failed to record worked location", error);
  });
}

function timesheetLocations(date, jobs = getAllJobsForDate(date)) {
  const key = toDateInput(date);
  const locations = new Set(Object.keys(state.workLocations[key] || {}));
  Object.entries(state.timeEntries[key] || {}).forEach(([location, minutes]) => {
    if (Number(minutes || 0) > 0) locations.add(location);
  });
  jobs.forEach((job) => {
    if (completedTaskCount(job) > 0 || isJobComplete(job)) locations.add(job.area);
  });
  return locationNames.filter((location) => locations.has(location));
}

function timeEntry(date, location) {
  return state.timeEntries[toDateInput(date)]?.[location] || 0;
}

function setTimeEntry(date, location, minutes, rerender = true) {
  const key = toDateInput(date);
  if (!state.timeEntries[key]) state.timeEntries[key] = {};
  const value = Math.max(0, Number.isFinite(minutes) ? minutes : 0);
  state.timeEntries[key][location] = value;
  api.timeEntries.set(key, location, value).catch((error) => {
    console.error("Failed to save time entry", error);
  });
  if (rerender) render();
}

function addTimeEntry(date, location, minutes, description = "") {
  const existing = timeEntry(date, location);
  setTimeEntry(date, location, existing + minutes, false);
  if (description) addExtraTimeDescription(date, location, description, minutes);
  render();
}

function addExtraTimeDescription(date, location, description, minutes = 0) {
  const key = toDateInput(date);
  if (!state.extraTimeEntries[key]) state.extraTimeEntries[key] = {};
  if (!state.extraTimeEntries[key][location]) state.extraTimeEntries[key][location] = [];
  state.extraTimeEntries[key][location].push(description);
  const id = `${key}-${location}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  api.extraTimeEntries.create({ id, date: key, location, minutes, description }).catch((error) => {
    console.error("Failed to save extra time entry", error);
  });
}

function timesheetTotal(date) {
  return Object.values(state.timeEntries[toDateInput(date)] || {}).reduce((sum, value) => sum + Number(value || 0), 0);
}

function weeklyTimesheetRows(date) {
  const start = weekStartDate(date);
  const end = addDaysToDate(start, 6);
  const rows = [];
  Object.entries(state.timeEntries).forEach(([day, entries]) => {
    if (day < start || day > end) return;
    locationNames.forEach((location) => {
      const minutes = Number(entries?.[location] || 0);
      if (minutes > 0) rows.push({ date: day, location, minutes, jobs: completedJobsForLocation(parseDate(day), location) });
    });
  });
  return rows.sort((a, b) => a.date.localeCompare(b.date) || locationNames.indexOf(a.location) - locationNames.indexOf(b.location));
}

function weeklyTimesheetTotal(date) {
  return weeklyTimesheetRows(date).reduce((sum, row) => sum + row.minutes, 0);
}

function plannedMinutesForDate(date) {
  const day = String(date.getDay());
  const config = state.config.workingDays[day];
  return config?.enabled ? Number(config.minutes || 0) : 0;
}

function weeklyPlannedMinutes(date) {
  return weekDates(date).reduce((sum, day) => sum + plannedMinutesForDate(parseDate(day)), 0);
}

function weeklyLocationTotal(date, location) {
  return weekDates(date).reduce((sum, day) => sum + timeEntry(parseDate(day), location), 0);
}

function weekDates(date) {
  const start = weekStartDate(date);
  return Array.from({ length: 7 }, (_, index) => addDaysToDate(start, index));
}

function formatTimesheetHours(minutes) {
  const value = Number(minutes || 0);
  if (!value) return "";
  const hours = value / 60;
  return Number.isInteger(hours) ? String(hours) : String(Math.round(hours * 100) / 100);
}

function completedJobsForLocation(date, location) {
  const extraDescriptions = state.extraTimeEntries[toDateInput(date)]?.[location] || [];
  if (extraDescriptions.length) return uniqueSorted(extraDescriptions);

  const completedJobs = getAllJobsForDate(date)
    .filter((job) => job.area === location && completedTaskCountOnDate(job, date) > 0)
    .map((job) => job.title);
  if (completedJobs.length) return uniqueSorted(completedJobs);

  return [];
}

function weeklyJobListMarkup(jobs) {
  if (!jobs.length) {
    return `
      <div class="timesheet-job-label">Jobs</div>
      <ul class="timesheet-job-list"><li>Time entered without a job note</li></ul>
    `;
  }
  return `
    <div class="timesheet-job-label">Jobs</div>
    <ul class="timesheet-job-list">
      ${jobs.map((job) => `<li>${escapeHtml(job)}</li>`).join("")}
    </ul>
  `;
}

function uniqueSorted(items) {
  return [...new Set(items)].sort((a, b) => a.localeCompare(b));
}

function parseTimeEntry(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return 0;
  if (/^\d{1,2}:\d{1,2}$/.test(raw)) {
    const [hours, minutes] = raw.split(":").map(Number);
    if (minutes >= 60) return null;
    return hours * 60 + minutes;
  }
  const hoursMatch = raw.match(/(\d+(?:\.\d+)?)\s*(h|hr|hrs|hour|hours)\b/);
  const minutesMatch = raw.match(/(\d+)\s*(m|min|mins|minute|minutes)\b/);
  if (hoursMatch || minutesMatch) {
    const hours = hoursMatch ? Number(hoursMatch[1]) : 0;
    const bareMinutes = hoursMatch && !minutesMatch
      ? raw.replace(hoursMatch[0], "").match(/\b(\d{1,2})\b/)
      : null;
    const minutes = minutesMatch ? Number(minutesMatch[1]) : bareMinutes ? Number(bareMinutes[1]) : 0;
    if (minutes >= 60) return null;
    return Math.round(hours * 60 + minutes);
  }
  if (/^\d+(?:\.\d+)?$/.test(raw)) {
    const number = Number(raw);
    return number <= 12 ? Math.round(number * 60) : Math.round(number);
  }
  return null;
}

function formatTimeEntryValue(minutes) {
  const value = Math.max(0, Number(minutes || 0));
  return value ? formatMinutes(value) : "";
}

function refreshTimesheetTotals(date) {
  const total = timesheetTotal(date);
  const planned = plannedMinutesForDate(date);
  const remaining = planned - total;
  const status = timesheetStatus(remaining);
  const totalNode = els.timesheetPanel.querySelector(".time-total");
  const hintNode = els.timesheetPanel.querySelector(".time-hint");
  if (totalNode) {
    totalNode.textContent = `${formatMinutes(total)} / ${formatMinutes(planned)}`;
    totalNode.classList.toggle("is-balanced", status.className === "is-balanced");
    totalNode.classList.toggle("is-under", status.className === "is-under");
    totalNode.classList.toggle("is-overtime", status.className === "is-overtime");
  }
  if (hintNode) {
    hintNode.textContent = status.message;
    hintNode.classList.toggle("is-balanced", status.className === "is-balanced");
    hintNode.classList.toggle("is-under", status.className === "is-under");
    hintNode.classList.toggle("is-overtime", status.className === "is-overtime");
  }
}

function timesheetStatus(remaining) {
  if (remaining === 0) {
    return {
      className: "is-balanced",
      message: "Timesheet balances to the expected day."
    };
  }
  if (remaining > 0) {
    return {
      className: "is-under",
      message: `${formatMinutes(remaining)} still to allocate.`
    };
  }
  return {
    className: "is-overtime",
    message: `Overtime: ${formatMinutes(Math.abs(remaining))}. Adjust the time entries if this should match the paid hours for the day.`
  };
}

function noteLineContext(note) {
  const parts = [];
  if (note.jobTitle) parts.push(note.jobTitle);
  if (note.taskText) parts.push(note.taskText);
  if (note.location && !parts.includes(note.location)) parts.push(note.location);
  return parts.join(" / ") || "General note";
}

function buildNoteSummary(notes) {
  if (!notes.length) {
    return "No repair issues or additional observations were recorded today.";
  }

  const safetyNotes = notes.filter((note) => note.priority === "Safety concern");
  const contractorNotes = notes.filter((note) => note.priority === "Needs contractor");
  const routineNotes = notes.filter((note) => note.priority === "Routine");
  const sentences = [];

  if (safetyNotes.length) {
    sentences.push(`${safetyNotes.length} safety ${plural(safetyNotes.length, "concern", "concerns")} recorded and should be reviewed as a priority.`);
  }
  if (contractorNotes.length) {
    sentences.push(`${contractorNotes.length} ${plural(contractorNotes.length, "item", "items")} may need contractor follow-up.`);
  }
  if (routineNotes.length) {
    sentences.push(`${routineNotes.length} routine ${plural(routineNotes.length, "observation", "observations")} noted for the record.`);
  }

  const examples = notes.slice(0, 2).map((note) => `${noteLineContext(note)}: ${note.text}`);
  return `${sentences.join(" ")} Key detail${examples.length === 1 ? "" : "s"}: ${examples.join(" | ")}.`;
}

function dictateInto(textarea) {
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SpeechRecognition) {
    textarea.focus();
    els.speechHint.textContent = "Speech button is not available in this browser. Use the iPhone keyboard microphone in the note field.";
    return;
  }

  const recognition = new SpeechRecognition();
  recognition.lang = "en-GB";
  recognition.interimResults = false;
  recognition.continuous = false;
  recognition.onresult = (event) => {
    const spoken = event.results[0][0].transcript;
    textarea.value = `${textarea.value} ${spoken}`.trim();
  };
  recognition.onerror = () => {
    textarea.focus();
    els.speechHint.textContent = "Speech capture stopped. The keyboard microphone is still available.";
  };
  recognition.start();
}

async function copyReport() {
  await navigator.clipboard.writeText(els.report.value);
  els.copyReport.textContent = "Copied";
  setTimeout(() => {
    els.copyReport.textContent = "Copy text";
  }, 1500);
}

async function shareReport() {
  const file = buildReportPdfFile();
  if (navigator.canShare?.({ files: [file] }) && navigator.share) {
    await navigator.share({ title: "Thomas job list", text: els.report.value, files: [file] });
    return;
  }
  showPdfLink(file);
  await copyReport();
}

function downloadReportPdf() {
  const file = buildReportPdfFile();
  showPdfLink(file);
  const link = document.createElement("a");
  link.href = URL.createObjectURL(file);
  link.download = file.name;
  link.click();
}

function showPdfLink(file) {
  const url = URL.createObjectURL(file);
  els.pdfLink.href = url;
  els.pdfLink.download = file.name;
  els.pdfLink.classList.remove("is-hidden");
}

function buildReportPdfFile() {
  const date = selectedDate();
  const jobs = getAllJobsForDate(date);
  const notes = notesForDate(date);
  const pdf = createReportPdf(date, jobs, notes);
  const name = `thomas-job-list-${toDateInput(date)}.pdf`;
  return new File([pdf], name, { type: "application/pdf" });
}

function inferPriority(text) {
  return /unsafe|danger|crack|broken|contractor|repair|replace/i.test(text) ? "Needs contractor" : "Routine";
}

function scheduleLabel(template) {
  if (template.cadence === "once") return `Specific date, ${formatShortDate(parseDate(template.dueDate))}`;
  if (template.cadence === "weekly" && template.timing === "fixed") return `Weekly, ${dayNames[template.day]}`;
  if (template.cadence === "weekly") return "Weekly, flexible";
  if (template.cadence === "sixWeekly" && template.timing === "fixed") return `Six-weekly, ${dayNames[template.day]}`;
  if (template.cadence === "sixWeekly") return "Six-weekly, flexible";
  if (template.cadence === "sixMonthly" && template.timing === "fixed") return `Six-monthly, day ${template.dayOfMonth}`;
  if (template.cadence === "sixMonthly") return "Six-monthly, flexible";
  if (template.cadence === "quarterly" && template.timing === "fixed") return `Quarterly, day ${template.dayOfMonth}`;
  if (template.cadence === "quarterly") return "Quarterly, flexible";
  if (template.cadence === "annual" && template.timing === "fixed") return `Annual, day ${template.dayOfMonth}`;
  if (template.cadence === "annual") return "Annual, flexible";
  if (template.timing === "fixed") return `Monthly, day ${template.dayOfMonth}`;
  return "Monthly, flexible";
}

function cadenceLabel(cadence) {
  if (cadence === "sixWeekly") return "Six weekly";
  if (cadence === "sixMonthly") return "Six monthly";
  if (cadence === "once") return "Specific date";
  return cadence.charAt(0).toUpperCase() + cadence.slice(1);
}

function jobCardClass(job) {
  const cadenceClass = job.cadence === "sixWeekly"
    ? "six-weekly"
    : job.cadence === "sixMonthly"
      ? "six-monthly"
      : job.cadence;
  return `cadence-${cadenceClass}`;
}

function cadencePriority(job) {
  if (job.cadence === "once") return 0;
  // Sorted by period length, most frequent first:
  //   weekly (7d) < monthly (~30d) < sixWeekly (42d) < quarterly (~91d) <
  //   sixMonthly (~182d) < annual (365d)
  const order = { weekly: 1, monthly: 2, sixWeekly: 3, quarterly: 4, sixMonthly: 5, annual: 6 };
  return order[job.cadence] || 5;
}

function clamp(value, min, max) {
  if (Number.isNaN(value)) return min;
  return Math.min(max, Math.max(min, value));
}

function listOrNone(items) {
  return items.length ? items : ["- None"];
}

function plural(count, singular, pluralValue) {
  return count === 1 ? singular : pluralValue;
}

function formatMinutes(minutes) {
  const value = Math.max(0, Math.round(Number(minutes || 0)));
  const hours = Math.floor(value / 60);
  const mins = value % 60;
  if (!hours) return `${mins}m`;
  if (!mins) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

function createReportPdf(date, jobs, notes) {
  const pageWidth = 842;
  const pageHeight = 595;
  const margin = 30;
  const contentWidth = pageWidth - margin * 2;
  const pages = [];
  let commands = [];
  let y = pageHeight - margin;

  const jobsWorkedToday = jobs.filter((job) => completedTaskCountOnDate(job, date) > 0);
  const outstandingJobs = jobs.filter((job) => !isJobComplete(job));
  const totalMinutes = timesheetTotal(date);
  const plannedMinutes = plannedMinutesForDate(date);
  const reportDate = date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const weekStart = weekStartDate(date);
  const weekEnd = addDaysToDate(weekStart, 6);
  const weeklyTitle = `${formatShortDate(parseDate(weekStart))} to ${formatShortDate(parseDate(weekEnd))}`;

  function finishPage() {
    pages.push(commands.join("\n"));
    commands = [];
    y = pageHeight - margin;
  }

  function ensureSpace(height) {
    if (y - height < margin) finishPage();
  }

  function text(value, x, size = 10, style = "regular") {
    const font = style === "bold" ? "F2" : "F1";
    commands.push(`BT /${font} ${size} Tf ${x} ${y} Td (${pdfEscape(value)}) Tj ET`);
    y -= size + 5;
  }

  function textAt(value, x, textY, size = 10, style = "regular") {
    const font = style === "bold" ? "F2" : "F1";
    commands.push(`BT /${font} ${size} Tf ${x} ${textY} Td (${pdfEscape(value)}) Tj ET`);
  }

  function line(x1, y1, x2, y2) {
    commands.push(`${x1} ${y1} m ${x2} ${y2} l S`);
  }

  function row(cells, widths, options = {}) {
    const size = options.header ? 9 : 8;
    const wrapped = cells.map((cell, index) => wrapText(String(cell), Math.floor(widths[index] / (size * 0.52))));
    const lines = Math.max(...wrapped.map((cell) => cell.length));
    const rowHeight = options.height || Math.max(24, lines * (size + 4) + 12);
    ensureSpace(rowHeight + 4);
    const top = y;
    let x = margin;
    commands.push(`${options.header ? "0.88 0.93 0.90" : "1 1 1"} rg ${margin} ${top - rowHeight} ${contentWidth} ${rowHeight} re f`);
    commands.push("0.82 0.82 0.78 RG");
    line(margin, top, margin + contentWidth, top);
    line(margin, top - rowHeight, margin + contentWidth, top - rowHeight);
    widths.forEach((width, index) => {
      line(x, top, x, top - rowHeight);
      const cellLines = wrapped[index];
      let textY = top - 14;
      cellLines.forEach((cellLine) => {
        commands.push(`BT /${options.header ? "F2" : "F1"} ${size} Tf ${x + 6} ${textY} Td (${pdfEscape(cellLine)}) Tj ET`);
        textY -= size + 4;
      });
      x += width;
    });
    line(margin + contentWidth, top, margin + contentWidth, top - rowHeight);
    y = top - rowHeight - 8;
  }

  function timesheetColumnLabel(location) {
    return location
      .replace("Churchyard Area", "Churchyard")
      .replace("Memorial Gardens", "Memorial")
      .replace("Highway Maintenance", "Highway Maint.")
      .replace("Love Lane Play Area", "Love Lane Play Area");
  }

  function renderWeeklyTimesheetPage() {
    textAt("Cleobury Mortimer Town Council", pageWidth / 2 - 88, pageHeight - 45, 14, "bold");
    textAt("THOMAS MARSHALL", pageWidth / 2 - 55, pageHeight - 77, 13, "bold");
    textAt("WEEKLY TIME SHEET", margin, pageHeight - 105, 12, "bold");
    textAt(weeklyTitle, margin + 132, pageHeight - 105, 9);
    y = pageHeight - 124;

    const firstWidth = 95;
    const totalWidth = 48;
    const locationWidth = (contentWidth - firstWidth - totalWidth) / locationNames.length;
    const widths = [firstWidth, ...locationNames.map(() => locationWidth), totalWidth];
    row(["Day", ...locationNames.map(timesheetColumnLabel), "Total"], widths, { header: true, height: 34 });
    row(["Date", ...locationNames.map(() => "HRS"), "HRS"], widths, { header: true, height: 28 });
    weekDates(date).forEach((day) => {
      const dayDate = parseDate(day);
      const values = locationNames.map((location) => timeEntry(dayDate, location));
      const total = values.reduce((sum, value) => sum + value, 0);
      row([
        dayDate.toLocaleDateString("en-GB", { weekday: "long" }).toUpperCase(),
        ...values.map(formatTimesheetHours),
        formatTimesheetHours(total)
      ], widths, { height: 38 });
    });
    row([
      "TOTAL HRS",
      ...locationNames.map((location) => formatTimesheetHours(weeklyLocationTotal(date, location))),
      formatTimesheetHours(weeklyTimesheetTotal(date))
    ], widths, { header: true, height: 32 });
    textAt("Please detail all work undertaken under Highway, Other and Admin, also non-standard tasks under other headings so that work can be allocated on our accounts.", margin, y - 6, 7, "bold");
    textAt("Signed by Employee ................................................", margin, margin + 14, 8);
    textAt("Checked by Line Manager ................................................", pageWidth / 2 + 70, margin + 14, 8);
    finishPage();
  }

  renderWeeklyTimesheetPage();

  text("Thomas job list", margin, 20, "bold");
  text(reportDate, margin, 12);
  y -= 6;
  text(`${jobsWorkedToday.length} jobs worked today · ${outstandingJobs.length} open · ${notes.length} notes · ${formatMinutes(totalMinutes)} timesheet`, margin, 10, "bold");
  y -= 12;

  text("Jobs", margin, 14, "bold");
  row(["Job", "Area", "Status", "Progress"], [290, 150, 110, 232], { header: true });
  jobs.forEach((job) => {
    const complete = isJobComplete(job);
    const remaining = job.tasks.filter((_, index) => getCompletion(job).tasks[index] !== true);
    row([
      job.title,
      job.area,
      complete ? "Complete" : "Open",
      `${completedTaskCount(job)}/${job.tasks.length} total. ${completedTaskCountOnDate(job, date)} ticked today.${complete ? "" : ` Left: ${remaining.join(", ")}`}`
    ], [290, 150, 110, 232]);
  });

  y -= 8;
  text("Daily timesheet", margin, 14, "bold");
  row(["Location", "Time"], [contentWidth - 130, 130], { header: true });
  const dailyTimesheet = timesheetLocations(date, jobs).map((location) => [location, formatMinutes(timeEntry(date, location))]);
  if (!dailyTimesheet.length) {
    row(["No locations logged", ""], [contentWidth - 130, 130]);
  } else {
    dailyTimesheet.forEach(([location, time]) => row([location, time], [contentWidth - 130, 130]));
    row(["Total", `${formatMinutes(totalMinutes)} / ${formatMinutes(plannedMinutes)}`], [contentWidth - 130, 130]);
  }

  y -= 8;
  text("Notes", margin, 14, "bold");
  if (!notes.length) {
    row(["No notes recorded"], [contentWidth]);
  } else {
    row(["Job / task", "Note"], [260, contentWidth - 260], { header: true });
    notes.forEach((note) => row([noteLineContext(note), note.text], [260, contentWidth - 260]));
  }

  finishPage();
  return buildPdf(pages, pageWidth, pageHeight);
}

function buildPdf(pageStreams, pageWidth, pageHeight) {
  const objects = [
    "<< /Type /Catalog /Pages 2 0 R >>",
    `<< /Type /Pages /Kids [${pageStreams.map((_, index) => `${3 + index * 2} 0 R`).join(" ")}] /Count ${pageStreams.length} >>`
  ];

  pageStreams.forEach((stream, index) => {
    const pageObject = 3 + index * 2;
    const streamObject = pageObject + 1;
    objects.push(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> /F2 << /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >> >> >> /Contents ${streamObject} 0 R >>`);
    objects.push(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
  });

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  return new Blob([pdf], { type: "application/pdf" });
}

function wrapText(value, maxChars) {
  const words = value.replace(/\s+/g, " ").trim().split(" ");
  const lines = [];
  let line = "";
  words.forEach((word) => {
    const next = line ? `${line} ${word}` : word;
    if (next.length > maxChars && line) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  });
  if (line) lines.push(line);
  return lines.length ? lines.slice(0, 8) : [""];
}

function pdfEscape(value) {
  return String(value)
    .replace(/[^\x20-\x7E]/g, " ")
    .replaceAll("\\", "\\\\")
    .replaceAll("(", "\\(")
    .replaceAll(")", "\\)");
}

function toDateInput(date) {
  const local = new Date(date);
  local.setMinutes(local.getMinutes() - local.getTimezoneOffset());
  return local.toISOString().slice(0, 10);
}

function parseDate(value) {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function addPeriodStart(value, days) {
  return toDateInput(addDays(parseDate(value), days));
}

function addDaysToDate(value, days) {
  return toDateInput(addDays(parseDate(value), days));
}

function addMonths(date, months) {
  return new Date(date.getFullYear(), date.getMonth() + months, date.getDate());
}

function addMonthsToDate(value, months) {
  return toDateInput(addMonths(parseDate(value), months));
}

function daysInMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

function weekStartDate(date) {
  const start = addDays(date, -((date.getDay() + 6) % 7));
  return toDateInput(start);
}

function weekKey(date) {
  return weekStartDate(date);
}

// sixWeekStartDate was replaced by sixWeeklyPeriod() which anchors to
// 1 January each year so the cycle restarts cleanly at year boundaries.

function sixMonthStartDate(date) {
  const month = date.getMonth() < 6 ? 0 : 6;
  return toDateInput(new Date(date.getFullYear(), month, 1));
}

function quarterStartDate(date) {
  const quarterMonth = Math.floor(date.getMonth() / 3) * 3;
  return toDateInput(new Date(date.getFullYear(), quarterMonth, 1));
}

function quarterKey(date) {
  return `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}`;
}

function formatShortDate(date) {
  return date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}
